import { supabase } from '@/integrations/supabase/client';

interface NotificationData {
  to: string;
  userId?: string;
  variables: Record<string, any>;
}

interface EmailNotificationResult {
  success: boolean;
  messageId?: string;
  error?: string;
  disabled?: boolean;
}

export class EmailNotificationService {
  private static readonly PLATFORM_URL = 'https://floridahighwaymenauction.com';

  /**
   * Generic method to send any email notification
   */
  private static async sendNotification(
    templateKey: string,
    data: NotificationData,
    fallbackSubject?: string
  ): Promise<EmailNotificationResult> {
    try {
      const { data: result, error } = await supabase.functions.invoke('send-email-notification', {
        body: {
          to: data.to,
          template: templateKey,
          variables: {
            ...data.variables,
            platform_url: this.PLATFORM_URL
          },
          user_id: data.userId,
          email_type: templateKey,
          ...(fallbackSubject && { subject: fallbackSubject })
        }
      });

      if (error) {
        console.error(`Failed to send ${templateKey} notification:`, error);
        return { success: false, error: error.message };
      }

      if (result?.disabled) {
        return { success: true, disabled: true };
      }

      return { 
        success: true, 
        messageId: result?.message_id 
      };
    } catch (error: any) {
      console.error(`Error sending ${templateKey} notification:`, error);
      return { success: false, error: error.message };
    }
  }



  /**
   * Send bid confirmation notification when user places a bid
   */
  static async sendBidConfirmationNotification(data: {
    userEmail: string;
    userId?: string;
    userName: string;
    artworkTitle: string;
    artistName: string;
    lotNumber: string;
    bidAmount: number;
    currentHighBid: number;
    auctionTitle: string;
    auctionEndTime: string;
    lotId: string;
    artworkImage?: string;
  }): Promise<EmailNotificationResult> {
    console.log('🚀 EmailService: Starting sendBidConfirmationNotification with data:', data);
    // Generate clean, modern white-themed email template
    const bidPlacedTemplate = 
      '<!DOCTYPE html>' +
      '<html lang="en">' +
      '<head>' +
        '<meta charset="UTF-8">' +
        '<meta name="viewport" content="width=device-width, initial-scale=1.0">' +
        '<title>Bid Confirmed - Florida Highwaymen Auctions</title>' +
      '</head>' +
      '<body style="margin: 0; padding: 0; background-color: #f9f9f9; font-family: -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, \'Helvetica Neue\', Arial, sans-serif;">' +
        '<div style="max-width: 600px; margin: 0 auto; background-color: #ffffff;">' +
          
          // Clean header
          '<div style="background-color: #ffffff; padding: 40px 40px 20px 40px; border-bottom: 1px solid #e5e5e5;">' +
            '<h1 style="margin: 0; color: #8B4513; font-size: 24px; font-weight: 300; letter-spacing: 2px; text-align: center;">FLORIDA HIGHWAYMEN AUCTIONS</h1>' +
            '<p style="margin: 8px 0 0 0; color: #666666; font-size: 14px; text-align: center; font-weight: 300;">Celebrating the Legacy of American Art</p>' +
          '</div>' +
          
          // Status header
          '<div style="background-color: #ffffff; padding: 30px 40px 10px 40px;">' +
            '<h2 style="margin: 0; color: #2d2d2d; font-size: 28px; font-weight: 400; text-align: center;">Bid Confirmation</h2>' +
            '<p style="margin: 15px 0 0 0; color: #22C55E; font-size: 16px; text-align: center; font-weight: 600;">🎯 Your bid has been successfully submitted - INSTANT PROCESSING!</p>' +
          '</div>' +
          
          // Main content
          '<div style="padding: 20px 40px 40px 40px; background-color: #ffffff;">' +
            '<p style="margin: 0 0 30px 0; font-size: 16px; color: #2d2d2d; line-height: 1.6;">Dear {{bidder_name}},</p>' +
            
            // Artwork section with image
            '<div style="border: 1px solid #e5e5e5; border-radius: 8px; overflow: hidden; margin: 30px 0;">' +
              '<div style="padding: 25px;">' +
                '<div style="display: table; width: 100%;">' +
                  '<div style="display: table-cell; vertical-align: top; width: 120px; padding-right: 20px;">' +
                    '<img src="{{artwork_image}}" alt="{{lot_title}}" style="width: 100px; height: 100px; object-fit: cover; border-radius: 4px; border: 1px solid #e5e5e5;" />' +
                  '</div>' +
                  '<div style="display: table-cell; vertical-align: top;">' +
                    '<h3 style="margin: 0 0 5px 0; color: #2d2d2d; font-size: 20px; font-weight: 500;">{{lot_title}}</h3>' +
                    '<p style="margin: 0 0 15px 0; color: #8B4513; font-size: 14px; font-style: italic;">by {{artist_name}}</p>' +
                    '<div style="border-top: 1px solid #f0f0f0; padding-top: 15px; margin-top: 15px;">' +
                      '<div style="margin-bottom: 8px;">' +
                        '<span style="color: #666666; font-size: 14px; display: inline-block; width: 120px;">Lot Number:</span>' +
                        '<span style="color: #2d2d2d; font-size: 14px; font-weight: 500;">#{{lot_number}}</span>' +
                      '</div>' +
                      '<div style="margin-bottom: 8px;">' +
                        '<span style="color: #666666; font-size: 14px; display: inline-block; width: 120px;">Your Bid:</span>' +
                        '<span style="color: #8B4513; font-size: 16px; font-weight: 600;">${{bid_amount}}</span>' +
                      '</div>' +
                      '<div style="margin-bottom: 8px;">' +
                        '<span style="color: #666666; font-size: 14px; display: inline-block; width: 120px;">Current High:</span>' +
                        '<span style="color: #2d2d2d; font-size: 14px; font-weight: 500;">${{current_high_bid}}</span>' +
                      '</div>' +
                      '<div>' +
                        '<span style="color: #666666; font-size: 14px; display: inline-block; width: 120px;">Auction:</span>' +
                        '<span style="color: #2d2d2d; font-size: 14px;">{{auction_title}}</span>' +
                      '</div>' +
                    '</div>' +
                  '</div>' +
                '</div>' +
              '</div>' +
            '</div>' +
            
            // Status message
            '<div style="background-color: #f8f8f8; border-left: 4px solid #8B4513; padding: 20px; margin: 30px 0;">' +
              '<h4 style="margin: 0 0 12px 0; color: #2d2d2d; font-size: 16px; font-weight: 500;">Next Steps</h4>' +
              '<ul style="margin: 0; padding-left: 18px; color: #666666; font-size: 14px; line-height: 1.6;">' +
                '<li style="margin-bottom: 5px;">Your bid is pending admin approval</li>' +
                '<li style="margin-bottom: 5px;">You\'ll receive immediate confirmation when approved</li>' +
                '<li style="margin-bottom: 5px;">We\'ll notify you if another bidder outbids you</li>' +
                '<li>Monitor the auction until {{auction_end_time}}</li>' +
              '</ul>' +
            '</div>' +
            
            // CTA Button
            '<div style="text-align: center; margin: 40px 0 30px 0;">' +
              '<a href="{{lot_url}}" style="display: inline-block; background-color: #8B4513; color: #ffffff; padding: 14px 32px; text-decoration: none; border-radius: 4px; font-size: 14px; font-weight: 500; letter-spacing: 1px; text-transform: uppercase;">View Lot Details</a>' +
            '</div>' +
          '</div>' +
          
          // Footer
          '<div style="background-color: #f8f8f8; padding: 30px 40px; text-align: center; border-top: 1px solid #e5e5e5;">' +
            '<p style="margin: 0 0 8px 0; color: #2d2d2d; font-size: 14px; font-weight: 500;">Florida Highwaymen Auctions LLC</p>' +
            '<p style="margin: 0 0 15px 0; color: #666666; font-size: 12px;">8210 Griffin Rd, Davie, FL 33328</p>' +
            '<p style="margin: 0; color: #666666; font-size: 12px;">Questions? <a href="mailto:info@floridahighwaymenauction.com" style="color: #8B4513; text-decoration: none;">info@floridahighwaymenauction.com</a></p>' +
          '</div>' +
        '</div>' +
      '</body>' +
      '</html>';

    // Replace variables in template
    console.log('🔄 EmailService: Replacing template variables...');
    let htmlContent = bidPlacedTemplate
      .replace(/{{bidder_name}}/g, data.userName)
      .replace(/{{lot_title}}/g, data.artworkTitle)
      .replace(/{{artist_name}}/g, data.artistName)
      .replace(/{{lot_number}}/g, data.lotNumber)
      .replace(/{{bid_amount}}/g, data.bidAmount.toLocaleString())
      .replace(/{{current_high_bid}}/g, data.currentHighBid.toLocaleString())
      .replace(/{{auction_title}}/g, data.auctionTitle)
      .replace(/{{auction_end_time}}/g, data.auctionEndTime)
      .replace(/{{artwork_image}}/g, data.artworkImage || 'https://placehold.co/100x100/8B4513/ffffff/png?text=ARTWORK&font=roboto')
      .replace(/{{lot_url}}/g, `${this.PLATFORM_URL}/lots/${data.lotId}`);
    
    console.log('✅ EmailService: Template variables replaced successfully');

    const subject = `Bid Confirmed - ${data.artworkTitle}`;

    console.log('📧 EmailService: Calling sendCustomEmail with subject:', subject);
    return this.sendCustomEmail({
      to: data.userEmail,
      subject,
      htmlContent,
      emailType: 'bid_placed',
      userId: data.userId
    });
  }

  /**
   * Send bid approval notification when admin approves a bid
   */
  static async sendBidApprovalNotification(data: {
    userEmail: string;
    userId?: string;
    userName: string;
    artworkTitle: string;
    artistName: string;
    lotNumber: string;
    bidAmount: number;
    auctionTitle: string;
    lotId: string;
    artworkImage?: string;
  }): Promise<EmailNotificationResult> {
    // Generate clean, modern white-themed approval email template
    const bidApprovedTemplate = 
      '<!DOCTYPE html>' +
      '<html lang="en">' +
      '<head>' +
        '<meta charset="UTF-8">' +
        '<meta name="viewport" content="width=device-width, initial-scale=1.0">' +
        '<title>Bid Approved - You\'re the High Bidder! - Florida Highwaymen Auctions</title>' +
      '</head>' +
      '<body style="margin: 0; padding: 0; background-color: #f9f9f9; font-family: -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, \'Helvetica Neue\', Arial, sans-serif;">' +
        '<div style="max-width: 600px; margin: 0 auto; background-color: #ffffff;">' +
          
          // Clean header
          '<div style="background-color: #ffffff; padding: 40px 40px 20px 40px; border-bottom: 1px solid #e5e5e5;">' +
            '<h1 style="margin: 0; color: #8B4513; font-size: 24px; font-weight: 300; letter-spacing: 2px; text-align: center;">FLORIDA HIGHWAYMEN AUCTIONS</h1>' +
            '<p style="margin: 8px 0 0 0; color: #666666; font-size: 14px; text-align: center; font-weight: 300;">Celebrating the Legacy of American Art</p>' +
          '</div>' +
          
          // Status header - success theme
          '<div style="background-color: #ffffff; padding: 30px 40px 10px 40px;">' +
            '<h2 style="margin: 0; color: #2d2d2d; font-size: 28px; font-weight: 400; text-align: center;">Congratulations!</h2>' +
            '<p style="margin: 15px 0 0 0; color: #8B4513; font-size: 16px; text-align: center; font-weight: 500;">Your bid has been approved - You\'re the high bidder</p>' +
          '</div>' +
          
          // Winner status badge
          '<div style="background-color: #ffffff; padding: 0 40px 20px 40px; text-align: center;">' +
            '<div style="display: inline-block; background-color: #8B4513; color: #ffffff; padding: 8px 20px; border-radius: 20px; font-size: 12px; font-weight: 600; letter-spacing: 1px;">HIGH BIDDER</div>' +
          '</div>' +
          
          // Main content
          '<div style="padding: 20px 40px 40px 40px; background-color: #ffffff;">' +
            '<p style="margin: 0 0 30px 0; font-size: 16px; color: #2d2d2d; line-height: 1.6;">Dear {{bidder_name}},</p>' +
            
            // Artwork section with image - winner styling
            '<div style="border: 2px solid #8B4513; border-radius: 8px; overflow: hidden; margin: 30px 0; position: relative;">' +
              '<div style="background-color: #8B4513; color: #ffffff; padding: 8px 15px; font-size: 11px; font-weight: 600; letter-spacing: 1px; text-align: center;">WINNING LOT</div>' +
              '<div style="padding: 25px;">' +
                '<div style="display: table; width: 100%;">' +
                  '<div style="display: table-cell; vertical-align: top; width: 120px; padding-right: 20px;">' +
                    '<img src="{{artwork_image}}" alt="{{lot_title}}" style="width: 100px; height: 100px; object-fit: cover; border-radius: 4px; border: 1px solid #e5e5e5;" />' +
                  '</div>' +
                  '<div style="display: table-cell; vertical-align: top;">' +
                    '<h3 style="margin: 0 0 5px 0; color: #2d2d2d; font-size: 20px; font-weight: 500;">{{lot_title}}</h3>' +
                    '<p style="margin: 0 0 15px 0; color: #8B4513; font-size: 14px; font-style: italic;">by {{artist_name}}</p>' +
                    '<div style="border-top: 1px solid #f0f0f0; padding-top: 15px; margin-top: 15px;">' +
                      '<div style="margin-bottom: 8px;">' +
                        '<span style="color: #666666; font-size: 14px; display: inline-block; width: 120px;">Lot Number:</span>' +
                        '<span style="color: #2d2d2d; font-size: 14px; font-weight: 500;">#{{lot_number}}</span>' +
                      '</div>' +
                      '<div style="margin-bottom: 8px;">' +
                        '<span style="color: #666666; font-size: 14px; display: inline-block; width: 120px;">Winning Bid:</span>' +
                        '<span style="color: #8B4513; font-size: 18px; font-weight: 700;">${{bid_amount}}</span>' +
                      '</div>' +
                      '<div>' +
                        '<span style="color: #666666; font-size: 14px; display: inline-block; width: 120px;">Auction:</span>' +
                        '<span style="color: #2d2d2d; font-size: 14px;">{{auction_title}}</span>' +
                      '</div>' +
                    '</div>' +
                  '</div>' +
                '</div>' +
              '</div>' +
            '</div>' +
            
            // Status message
            '<div style="background-color: #f8f8f8; border-left: 4px solid #8B4513; padding: 20px; margin: 30px 0;">' +
              '<h4 style="margin: 0 0 12px 0; color: #2d2d2d; font-size: 16px; font-weight: 500;">Important Notice</h4>' +
              '<ul style="margin: 0; padding-left: 18px; color: #666666; font-size: 14px; line-height: 1.6;">' +
                '<li style="margin-bottom: 5px;">You are currently the high bidder on this lot</li>' +
                '<li style="margin-bottom: 5px;">We\'ll notify you immediately if another bidder outbids you</li>' +
                '<li style="margin-bottom: 5px;">Monitor the auction closely until it ends</li>' +
                '<li>If you win, we\'ll send payment and collection instructions</li>' +
              '</ul>' +
            '</div>' +
            
            // CTA Buttons
            '<div style="text-align: center; margin: 40px 0 30px 0;">' +
              '<a href="{{lot_url}}" style="display: inline-block; background-color: #8B4513; color: #ffffff; padding: 14px 32px; text-decoration: none; border-radius: 4px; font-size: 14px; font-weight: 500; letter-spacing: 1px; text-transform: uppercase; margin-right: 15px;">Monitor This Lot</a>' +
              '<a href="' + this.PLATFORM_URL + '/auctions" style="display: inline-block; background-color: #2d2d2d; color: #ffffff; padding: 14px 32px; text-decoration: none; border-radius: 4px; font-size: 14px; font-weight: 500; letter-spacing: 1px; text-transform: uppercase;">Browse More Lots</a>' +
            '</div>' +
          '</div>' +
          
          // Footer
          '<div style="background-color: #f8f8f8; padding: 30px 40px; text-align: center; border-top: 1px solid #e5e5e5;">' +
            '<p style="margin: 0 0 8px 0; color: #2d2d2d; font-size: 14px; font-weight: 500;">Florida Highwaymen Auctions LLC</p>' +
            '<p style="margin: 0 0 15px 0; color: #666666; font-size: 12px;">8210 Griffin Rd, Davie, FL 33328</p>' +
            '<p style="margin: 0; color: #666666; font-size: 12px;">Questions? <a href="mailto:info@floridahighwaymenauction.com" style="color: #8B4513; text-decoration: none;">info@floridahighwaymenauction.com</a></p>' +
          '</div>' +
        '</div>' +
      '</body>' +
      '</html>';

    // Replace variables in template
    let htmlContent = bidApprovedTemplate
      .replace(/{{bidder_name}}/g, data.userName)
      .replace(/{{lot_title}}/g, data.artworkTitle)
      .replace(/{{artist_name}}/g, data.artistName)
      .replace(/{{lot_number}}/g, data.lotNumber)
      .replace(/{{bid_amount}}/g, data.bidAmount.toLocaleString())
      .replace(/{{auction_title}}/g, data.auctionTitle)
      .replace(/{{artwork_image}}/g, data.artworkImage || 'https://placehold.co/100x100/8B4513/ffffff/png?text=ARTWORK&font=roboto')
      .replace(/{{lot_url}}/g, `${this.PLATFORM_URL}/lots/${data.lotId}`);

    const subject = `🎉 Bid Approved - You're the High Bidder on ${data.artworkTitle}!`;

    return this.sendCustomEmail({
      to: data.userEmail,
      subject,
      htmlContent,
      emailType: 'bid_approved',
      userId: data.userId
    });
  }

  /**
   * Send bid denial notification when admin rejects a bid
   */
  static async sendBidDenialNotification(data: {
    userEmail: string;
    userId?: string;
    userName: string;
    artworkTitle: string;
    artistName: string;
    lotNumber: string;
    bidAmount: number;
    rejectionReason: string;
    auctionTitle: string;
    lotId: string;
    artworkImage?: string;
  }): Promise<EmailNotificationResult> {
    // Generate clean, modern white-themed denial email template
    const bidDeniedTemplate = 
      '<!DOCTYPE html>' +
      '<html lang="en">' +
      '<head>' +
        '<meta charset="UTF-8">' +
        '<meta name="viewport" content="width=device-width, initial-scale=1.0">' +
        '<title>Bid Not Approved - Florida Highwaymen Auctions</title>' +
      '</head>' +
      '<body style="margin: 0; padding: 0; background-color: #f9f9f9; font-family: -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, \'Helvetica Neue\', Arial, sans-serif;">' +
        '<div style="max-width: 600px; margin: 0 auto; background-color: #ffffff;">' +
          
          // Clean header
          '<div style="background-color: #ffffff; padding: 40px 40px 20px 40px; border-bottom: 1px solid #e5e5e5;">' +
            '<h1 style="margin: 0; color: #8B4513; font-size: 24px; font-weight: 300; letter-spacing: 2px; text-align: center;">FLORIDA HIGHWAYMEN AUCTIONS</h1>' +
            '<p style="margin: 8px 0 0 0; color: #666666; font-size: 14px; text-align: center; font-weight: 300;">Celebrating the Legacy of American Art</p>' +
          '</div>' +
          
          // Status header
          '<div style="background-color: #ffffff; padding: 30px 40px 10px 40px;">' +
            '<h2 style="margin: 0; color: #2d2d2d; font-size: 28px; font-weight: 400; text-align: center;">Bid Not Approved</h2>' +
            '<p style="margin: 15px 0 0 0; color: #666666; font-size: 16px; text-align: center;">Your bid was not accepted for this lot</p>' +
          '</div>' +
          
          // Main content
          '<div style="padding: 20px 40px 40px 40px; background-color: #ffffff;">' +
            '<p style="margin: 0 0 30px 0; font-size: 16px; color: #2d2d2d; line-height: 1.6;">Dear {{bidder_name}},</p>' +
            '<p style="margin: 0 0 30px 0; font-size: 16px; color: #2d2d2d; line-height: 1.6;">We regret to inform you that your bid was not approved for the following lot:</p>' +
            
            // Artwork section with image
            '<div style="border: 1px solid #e5e5e5; border-radius: 8px; overflow: hidden; margin: 30px 0;">' +
              '<div style="padding: 25px;">' +
                '<div style="display: table; width: 100%;">' +
                  '<div style="display: table-cell; vertical-align: top; width: 120px; padding-right: 20px;">' +
                    '<img src="{{artwork_image}}" alt="{{lot_title}}" style="width: 100px; height: 100px; object-fit: cover; border-radius: 4px; border: 1px solid #e5e5e5;" />' +
                  '</div>' +
                  '<div style="display: table-cell; vertical-align: top;">' +
                    '<h3 style="margin: 0 0 5px 0; color: #2d2d2d; font-size: 20px; font-weight: 500;">{{lot_title}}</h3>' +
                    '<p style="margin: 0 0 15px 0; color: #8B4513; font-size: 14px; font-style: italic;">by {{artist_name}}</p>' +
                    '<div style="border-top: 1px solid #f0f0f0; padding-top: 15px; margin-top: 15px;">' +
                      '<div style="margin-bottom: 8px;">' +
                        '<span style="color: #666666; font-size: 14px; display: inline-block; width: 120px;">Lot Number:</span>' +
                        '<span style="color: #2d2d2d; font-size: 14px; font-weight: 500;">#{{lot_number}}</span>' +
                      '</div>' +
                      '<div style="margin-bottom: 8px;">' +
                        '<span style="color: #666666; font-size: 14px; display: inline-block; width: 120px;">Your Bid:</span>' +
                        '<span style="color: #666666; font-size: 16px; font-weight: 600;">${{bid_amount}}</span>' +
                      '</div>' +
                      '<div>' +
                        '<span style="color: #666666; font-size: 14px; display: inline-block; width: 120px;">Auction:</span>' +
                        '<span style="color: #2d2d2d; font-size: 14px;">{{auction_title}}</span>' +
                      '</div>' +
                    '</div>' +
                  '</div>' +
                '</div>' +
              '</div>' +
            '</div>' +
            
            // Rejection reason
            '<div style="background-color: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; padding: 20px; margin: 30px 0;">' +
              '<h4 style="margin: 0 0 12px 0; color: #991b1b; font-size: 16px; font-weight: 500;">Reason for Rejection</h4>' +
              '<p style="margin: 0; color: #991b1b; font-size: 14px; line-height: 1.6;">{{rejection_reason}}</p>' +
            '</div>' +
            
            // Next steps
            '<div style="background-color: #f8f8f8; border-left: 4px solid #8B4513; padding: 20px; margin: 30px 0;">' +
              '<h4 style="margin: 0 0 12px 0; color: #2d2d2d; font-size: 16px; font-weight: 500;">What You Can Do</h4>' +
              '<ul style="margin: 0; padding-left: 18px; color: #666666; font-size: 14px; line-height: 1.6;">' +
                '<li style="margin-bottom: 5px;">You can place a new bid if the lot is still active</li>' +
                '<li style="margin-bottom: 5px;">Browse other available lots in this auction</li>' +
                '<li style="margin-bottom: 5px;">Contact us if you have questions about the rejection</li>' +
                '<li>Review our bidding guidelines for future reference</li>' +
              '</ul>' +
            '</div>' +
            
            // CTA Buttons
            '<div style="text-align: center; margin: 40px 0 30px 0;">' +
              '<a href="{{lot_url}}" style="display: inline-block; background-color: #8B4513; color: #ffffff; padding: 14px 32px; text-decoration: none; border-radius: 4px; font-size: 14px; font-weight: 500; letter-spacing: 1px; text-transform: uppercase; margin-right: 15px;">View This Lot</a>' +
              '<a href="' + this.PLATFORM_URL + '/auctions" style="display: inline-block; background-color: #2d2d2d; color: #ffffff; padding: 14px 32px; text-decoration: none; border-radius: 4px; font-size: 14px; font-weight: 500; letter-spacing: 1px; text-transform: uppercase;">Browse Other Lots</a>' +
            '</div>' +
          '</div>' +
          
          // Footer
          '<div style="background-color: #f8f8f8; padding: 30px 40px; text-align: center; border-top: 1px solid #e5e5e5;">' +
            '<p style="margin: 0 0 8px 0; color: #2d2d2d; font-size: 14px; font-weight: 500;">Florida Highwaymen Auctions LLC</p>' +
            '<p style="margin: 0 0 15px 0; color: #666666; font-size: 12px;">8210 Griffin Rd, Davie, FL 33328</p>' +
            '<p style="margin: 0; color: #666666; font-size: 12px;">Questions? <a href="mailto:info@floridahighwaymenauction.com" style="color: #8B4513; text-decoration: none;">info@floridahighwaymenauction.com</a></p>' +
          '</div>' +
        '</div>' +
      '</body>' +
      '</html>';

    // Replace variables in template
    let htmlContent = bidDeniedTemplate
      .replace(/{{bidder_name}}/g, data.userName)
      .replace(/{{lot_title}}/g, data.artworkTitle)
      .replace(/{{artist_name}}/g, data.artistName)
      .replace(/{{lot_number}}/g, data.lotNumber)
      .replace(/{{bid_amount}}/g, data.bidAmount.toLocaleString())
      .replace(/{{rejection_reason}}/g, data.rejectionReason)
      .replace(/{{auction_title}}/g, data.auctionTitle)
      .replace(/{{artwork_image}}/g, data.artworkImage || 'https://placehold.co/100x100/8B4513/ffffff/png?text=ARTWORK&font=roboto')
      .replace(/{{lot_url}}/g, `${this.PLATFORM_URL}/lots/${data.lotId}`);

    const subject = `Bid Not Approved - ${data.artworkTitle}`;

    return this.sendCustomEmail({
      to: data.userEmail,
      subject,
      htmlContent,
      emailType: 'bid_denied',
      userId: data.userId
    });
  }

  /**
   * Send outbid notification when user is outbid by another bidder
   */
  static async sendOutbidNotification(data: {
    userEmail: string;
    userId?: string;
    userName: string;
    artworkTitle: string;
    artistName: string;
    lotNumber: string;
    yourBid: number;
    newHighBid: number;
    auctionTitle: string;
    lotId: string;
    artworkImage?: string;
  }): Promise<EmailNotificationResult> {
    // Generate clean, modern white-themed outbid email template
    const outbidTemplate = 
      '<!DOCTYPE html>' +
      '<html lang="en">' +
      '<head>' +
        '<meta charset="UTF-8">' +
        '<meta name="viewport" content="width=device-width, initial-scale=1.0">' +
        '<title>You\'ve Been Outbid - Florida Highwaymen Auctions</title>' +
      '</head>' +
      '<body style="margin: 0; padding: 0; background-color: #f9f9f9; font-family: -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, \'Helvetica Neue\', Arial, sans-serif;">' +
        '<div style="max-width: 600px; margin: 0 auto; background-color: #ffffff;">' +
          
          // Clean header
          '<div style="background-color: #ffffff; padding: 40px 40px 20px 40px; border-bottom: 1px solid #e5e5e5;">' +
            '<h1 style="margin: 0; color: #8B4513; font-size: 24px; font-weight: 300; letter-spacing: 2px; text-align: center;">FLORIDA HIGHWAYMEN AUCTIONS</h1>' +
            '<p style="margin: 8px 0 0 0; color: #666666; font-size: 14px; text-align: center; font-weight: 300;">Celebrating the Legacy of American Art</p>' +
          '</div>' +
          
          // Status header
          '<div style="background-color: #ffffff; padding: 30px 40px 10px 40px;">' +
            '<h2 style="margin: 0; color: #2d2d2d; font-size: 28px; font-weight: 400; text-align: center;">You\'ve Been Outbid</h2>' +
            '<p style="margin: 15px 0 0 0; color: #666666; font-size: 16px; text-align: center;">Another bidder has placed a higher bid</p>' +
          '</div>' +
          
          // Alert badge
          '<div style="background-color: #ffffff; padding: 0 40px 20px 40px; text-align: center;">' +
            '<div style="display: inline-block; background-color: #dc2626; color: #ffffff; padding: 8px 20px; border-radius: 20px; font-size: 12px; font-weight: 600; letter-spacing: 1px;">OUTBID</div>' +
          '</div>' +
          
          // Main content
          '<div style="padding: 20px 40px 40px 40px; background-color: #ffffff;">' +
            '<p style="margin: 0 0 30px 0; font-size: 16px; color: #2d2d2d; line-height: 1.6;">Dear {{bidder_name}},</p>' +
            '<p style="margin: 0 0 30px 0; font-size: 16px; color: #2d2d2d; line-height: 1.6;">Someone has placed a higher bid on the lot you were bidding on. Here are the details:</p>' +
            
            // Artwork section with image
            '<div style="border: 2px solid #dc2626; border-radius: 8px; overflow: hidden; margin: 30px 0; position: relative;">' +
              '<div style="background-color: #dc2626; color: #ffffff; padding: 8px 15px; font-size: 11px; font-weight: 600; letter-spacing: 1px; text-align: center;">OUTBID LOT</div>' +
              '<div style="padding: 25px;">' +
                '<div style="display: table; width: 100%;">' +
                  '<div style="display: table-cell; vertical-align: top; width: 120px; padding-right: 20px;">' +
                    '<img src="{{artwork_image}}" alt="{{lot_title}}" style="width: 100px; height: 100px; object-fit: cover; border-radius: 4px; border: 1px solid #e5e5e5;" />' +
                  '</div>' +
                  '<div style="display: table-cell; vertical-align: top;">' +
                    '<h3 style="margin: 0 0 5px 0; color: #2d2d2d; font-size: 20px; font-weight: 500;">{{lot_title}}</h3>' +
                    '<p style="margin: 0 0 15px 0; color: #8B4513; font-size: 14px; font-style: italic;">by {{artist_name}}</p>' +
                    '<div style="border-top: 1px solid #f0f0f0; padding-top: 15px; margin-top: 15px;">' +
                      '<div style="margin-bottom: 8px;">' +
                        '<span style="color: #666666; font-size: 14px; display: inline-block; width: 120px;">Lot Number:</span>' +
                        '<span style="color: #2d2d2d; font-size: 14px; font-weight: 500;">#{{lot_number}}</span>' +
                      '</div>' +
                      '<div style="margin-bottom: 8px;">' +
                        '<span style="color: #666666; font-size: 14px; display: inline-block; width: 120px;">Your Bid:</span>' +
                        '<span style="color: #666666; font-size: 16px; font-weight: 600;">${{your_bid}}</span>' +
                      '</div>' +
                      '<div style="margin-bottom: 8px;">' +
                        '<span style="color: #666666; font-size: 14px; display: inline-block; width: 120px;">New High Bid:</span>' +
                        '<span style="color: #dc2626; font-size: 18px; font-weight: 700;">${{new_high_bid}}</span>' +
                      '</div>' +
                      '<div>' +
                        '<span style="color: #666666; font-size: 14px; display: inline-block; width: 120px;">Auction:</span>' +
                        '<span style="color: #2d2d2d; font-size: 14px;">{{auction_title}}</span>' +
                      '</div>' +
                    '</div>' +
                  '</div>' +
                '</div>' +
              '</div>' +
            '</div>' +
            
            // Action needed
            '<div style="background-color: #fef3c7; border: 1px solid #fde68a; border-radius: 8px; padding: 20px; margin: 30px 0;">' +
              '<h4 style="margin: 0 0 12px 0; color: #92400e; font-size: 16px; font-weight: 500;">Want to Stay in the Running?</h4>' +
              '<p style="margin: 0; color: #92400e; font-size: 14px; line-height: 1.6;">You can place a higher bid to regain your position as the high bidder. Don\'t let this artwork slip away!</p>' +
            '</div>' +
            
            // CTA Buttons
            '<div style="text-align: center; margin: 40px 0 30px 0;">' +
              '<a href="{{lot_url}}" style="display: inline-block; background-color: #dc2626; color: #ffffff; padding: 14px 32px; text-decoration: none; border-radius: 4px; font-size: 14px; font-weight: 500; letter-spacing: 1px; text-transform: uppercase; margin-right: 15px;">Place Higher Bid</a>' +
              '<a href="' + this.PLATFORM_URL + '/auctions" style="display: inline-block; background-color: #8B4513; color: #ffffff; padding: 14px 32px; text-decoration: none; border-radius: 4px; font-size: 14px; font-weight: 500; letter-spacing: 1px; text-transform: uppercase;">Browse Other Lots</a>' +
            '</div>' +
          '</div>' +
          
          // Footer
          '<div style="background-color: #f8f8f8; padding: 30px 40px; text-align: center; border-top: 1px solid #e5e5e5;">' +
            '<p style="margin: 0 0 8px 0; color: #2d2d2d; font-size: 14px; font-weight: 500;">Florida Highwaymen Auctions LLC</p>' +
            '<p style="margin: 0 0 15px 0; color: #666666; font-size: 12px;">8210 Griffin Rd, Davie, FL 33328</p>' +
            '<p style="margin: 0; color: #666666; font-size: 12px;">Questions? <a href="mailto:info@floridahighwaymenauction.com" style="color: #8B4513; text-decoration: none;">info@floridahighwaymenauction.com</a></p>' +
          '</div>' +
        '</div>' +
      '</body>' +
      '</html>';

    // Replace variables in template
    let htmlContent = outbidTemplate
      .replace(/{{bidder_name}}/g, data.userName)
      .replace(/{{lot_title}}/g, data.artworkTitle)
      .replace(/{{artist_name}}/g, data.artistName)
      .replace(/{{lot_number}}/g, data.lotNumber)
      .replace(/{{your_bid}}/g, data.yourBid.toLocaleString())
      .replace(/{{new_high_bid}}/g, data.newHighBid.toLocaleString())
      .replace(/{{auction_title}}/g, data.auctionTitle)
      .replace(/{{artwork_image}}/g, data.artworkImage || 'https://placehold.co/100x100/8B4513/ffffff/png?text=ARTWORK&font=roboto')
      .replace(/{{lot_url}}/g, `${this.PLATFORM_URL}/lots/${data.lotId}`);

    const subject = `You've Been Outbid - ${data.artworkTitle}`;

    return this.sendCustomEmail({
      to: data.userEmail,
      subject,
      htmlContent,
      emailType: 'bid_outbid',
      userId: data.userId
    });
  }

  /**
   * Send PROXY outbid notification - when outbid by an automatic proxy bid
   * CRITICAL: Different messaging to explain proxy bidding
   */
  static async sendProxyOutbidNotification(data: {
    userEmail: string;
    userId?: string;
    userName: string;
    artworkTitle: string;
    artistName: string;
    lotNumber: string;
    yourBid: number;
    currentBid: number;
    lotId: string;
    proxyMaxAmount: string;
  }): Promise<EmailNotificationResult> {
    // Generate proxy-specific outbid email template
    const proxyOutbidTemplate = 
      '<!DOCTYPE html>' +
      '<html lang="en">' +
      '<head>' +
        '<meta charset="UTF-8">' +
        '<meta name="viewport" content="width=device-width, initial-scale=1.0">' +
        '<title>Outbid by Proxy - Florida Highwaymen Auctions</title>' +
      '</head>' +
      '<body style="margin: 0; padding: 0; background-color: #f9f9f9; font-family: -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, \'Helvetica Neue\', Arial, sans-serif;">' +
        '<div style="max-width: 600px; margin: 0 auto; background-color: #ffffff;">' +
          
          // Clean header
          '<div style="background-color: #ffffff; padding: 40px 40px 20px 40px; border-bottom: 1px solid #e5e5e5;">' +
            '<h1 style="margin: 0; color: #8B4513; font-size: 24px; font-weight: 300; letter-spacing: 2px; text-align: center;">FLORIDA HIGHWAYMEN AUCTIONS</h1>' +
            '<p style="margin: 8px 0 0 0; color: #666666; font-size: 14px; text-align: center; font-weight: 300;">Celebrating the Legacy of American Art</p>' +
          '</div>' +
          
          // Status header
          '<div style="background-color: #ffffff; padding: 30px 40px 10px 40px;">' +
            '<h2 style="margin: 0; color: #2d2d2d; font-size: 28px; font-weight: 400; text-align: center;">Outbid by Automatic Proxy</h2>' +
            '<p style="margin: 15px 0 0 0; color: #666666; font-size: 16px; text-align: center;">Another bidder\'s automatic proxy bid was executed</p>' +
          '</div>' +
          
          // Alert badge
          '<div style="background-color: #ffffff; padding: 0 40px 20px 40px; text-align: center;">' +
            '<div style="display: inline-block; background-color: #7c3aed; color: #ffffff; padding: 8px 20px; border-radius: 20px; font-size: 12px; font-weight: 600; letter-spacing: 1px;">🤖 PROXY BID</div>' +
          '</div>' +
          
          // Main content
          '<div style="padding: 20px 40px 40px 40px; background-color: #ffffff;">' +
            '<p style="margin: 0 0 20px 0; font-size: 16px; color: #2d2d2d; line-height: 1.6;">Dear {{bidder_name}},</p>' +
            '<p style="margin: 0 0 20px 0; font-size: 16px; color: #2d2d2d; line-height: 1.6;">You\'ve been outbid by another bidder\'s <strong>automatic proxy bid</strong>. Here\'s what happened:</p>' +
            
            // What is a proxy bid explanation
            '<div style="background-color: #f3f4f6; border-left: 4px solid #7c3aed; padding: 20px; margin: 20px 0;">' +
              '<h4 style="margin: 0 0 12px 0; color: #7c3aed; font-size: 15px; font-weight: 600;">🤖 What is a Proxy Bid?</h4>' +
              '<p style="margin: 0; color: #4b5563; font-size: 14px; line-height: 1.6;">A proxy bid is an automatic bidding system. The bidder set a maximum amount they\'re willing to pay, and our system automatically places bids on their behalf (one increment at a time) whenever they\'re outbid - until their maximum is reached.</p>' +
            '</div>' +
            
            // Bid details
            '<div style="border: 2px solid #7c3aed; border-radius: 8px; overflow: hidden; margin: 30px 0;">' +
              '<div style="background-color: #7c3aed; color: #ffffff; padding: 8px 15px; font-size: 11px; font-weight: 600; letter-spacing: 1px; text-align: center;">AUTOMATIC PROXY EXECUTION</div>' +
              '<div style="padding: 25px;">' +
                '<h3 style="margin: 0 0 5px 0; color: #2d2d2d; font-size: 20px; font-weight: 500;">{{lot_title}}</h3>' +
                '<p style="margin: 0 0 15px 0; color: #8B4513; font-size: 14px; font-style: italic;">by {{artist_name}}</p>' +
                '<div style="border-top: 1px solid #f0f0f0; padding-top: 15px; margin-top: 15px;">' +
                  '<div style="margin-bottom: 8px;">' +
                    '<span style="color: #666666; font-size: 14px; display: inline-block; width: 140px;">Lot Number:</span>' +
                    '<span style="color: #2d2d2d; font-size: 14px; font-weight: 500;">#{{lot_number}}</span>' +
                  '</div>' +
                  '<div style="margin-bottom: 8px;">' +
                    '<span style="color: #666666; font-size: 14px; display: inline-block; width: 140px;">Your Bid:</span>' +
                    '<span style="color: #666666; font-size: 16px; font-weight: 600;">${{your_bid}}</span>' +
                  '</div>' +
                  '<div style="margin-bottom: 8px;">' +
                    '<span style="color: #666666; font-size: 14px; display: inline-block; width: 140px;">Current Proxy Bid:</span>' +
                    '<span style="color: #7c3aed; font-size: 18px; font-weight: 700;">${{current_bid}}</span>' +
                  '</div>' +
                  '<div style="background-color: #fef3c7; border: 1px solid #fde68a; border-radius: 4px; padding: 10px; margin-top: 15px;">' +
                    '<p style="margin: 0; color: #92400e; font-size: 13px;"><strong>Note:</strong> This bidder may have authorized bids up to ${{proxy_max}} or higher. Their proxy will keep bidding automatically if outbid.</p>' +
                  '</div>' +
                '</div>' +
              '</div>' +
            '</div>' +
            
            // What can you do
            '<div style="background-color: #dbeafe; border: 1px solid #93c5fd; border-radius: 8px; padding: 20px; margin: 30px 0;">' +
              '<h4 style="margin: 0 0 12px 0; color: #1e40af; font-size: 16px; font-weight: 500;">💡 Want to Compete?</h4>' +
              '<p style="margin: 0 0 10px 0; color: #1e3a8a; font-size: 14px; line-height: 1.6;">You have two options:</p>' +
              '<ol style="margin: 0; padding-left: 18px; color: #1e3a8a; font-size: 14px; line-height: 1.8;">' +
                '<li><strong>Place a higher manual bid</strong> - But the proxy may automatically outbid you again</li>' +
                '<li><strong>Set your own proxy bid</strong> - Our system will automatically bid for you up to your maximum</li>' +
              '</ol>' +
            '</div>' +
            
            // Priority note
            '<div style="background-color: #f8f8f8; border-left: 4px solid #8B4513; padding: 20px; margin: 30px 0;">' +
              '<h4 style="margin: 0 0 12px 0; color: #2d2d2d; font-size: 15px; font-weight: 600;">⏰ Proxy Priority</h4>' +
              '<p style="margin: 0; color: #666666; font-size: 14px; line-height: 1.6;">If you set a proxy bid at the same maximum amount as another bidder, <strong>the FIRST proxy placed wins</strong>. Early birds get priority!</p>' +
            '</div>' +
            
            // CTA Buttons
            '<div style="text-align: center; margin: 40px 0 30px 0;">' +
              '<a href="{{lot_url}}" style="display: inline-block; background-color: #7c3aed; color: #ffffff; padding: 14px 32px; text-decoration: none; border-radius: 4px; font-size: 14px; font-weight: 500; letter-spacing: 1px; text-transform: uppercase; margin-right: 15px;">Set Proxy Bid</a>' +
              '<a href="' + this.PLATFORM_URL + '/auctions" style="display: inline-block; background-color: #8B4513; color: #ffffff; padding: 14px 32px; text-decoration: none; border-radius: 4px; font-size: 14px; font-weight: 500; letter-spacing: 1px; text-transform: uppercase;">Browse Other Lots</a>' +
            '</div>' +
          '</div>' +
          
          // Footer
          '<div style="background-color: #f8f8f8; padding: 30px 40px; text-align: center; border-top: 1px solid #e5e5e5;">' +
            '<p style="margin: 0 0 8px 0; color: #2d2d2d; font-size: 14px; font-weight: 500;">Florida Highwaymen Auctions LLC</p>' +
            '<p style="margin: 0 0 15px 0; color: #666666; font-size: 12px;">8210 Griffin Rd, Davie, FL 33328</p>' +
            '<p style="margin: 0; color: #666666; font-size: 12px;">Questions? <a href="mailto:info@floridahighwaymenauction.com" style="color: #8B4513; text-decoration: none;">info@floridahighwaymenauction.com</a></p>' +
          '</div>' +
        '</div>' +
      '</body>' +
      '</html>';

    // Replace variables in template
    let htmlContent = proxyOutbidTemplate
      .replace(/{{bidder_name}}/g, data.userName)
      .replace(/{{lot_title}}/g, data.artworkTitle)
      .replace(/{{artist_name}}/g, data.artistName)
      .replace(/{{lot_number}}/g, data.lotNumber)
      .replace(/{{your_bid}}/g, data.yourBid.toLocaleString())
      .replace(/{{current_bid}}/g, data.currentBid.toLocaleString())
      .replace(/{{proxy_max}}/g, data.proxyMaxAmount)
      .replace(/{{lot_url}}/g, `${this.PLATFORM_URL}/lots/${data.lotId}`);

    const subject = `🤖 Outbid by Automatic Proxy - ${data.artworkTitle}`;

    return this.sendCustomEmail({
      to: data.userEmail,
      subject,
      htmlContent,
      emailType: 'bid_outbid_proxy',
      userId: data.userId
    });
  }

  /**
   * Send winning bid notification
   */
  static async sendWinningBidNotification(data: {
    userEmail: string;
    userId?: string;
    userName: string;
    artworkTitle: string;
    artistName: string;
    lotNumber: string;
    winningBid: number;
  }): Promise<EmailNotificationResult> {
    return this.sendNotification('bid_won', {
      to: data.userEmail,
      userId: data.userId,
      variables: {
        user_name: data.userName,
        artwork_title: data.artworkTitle,
        artist_name: data.artistName,
        lot_number: data.lotNumber,
        winning_bid: data.winningBid.toLocaleString()
      }
    });
  }

  /**
   * Send auction starting notification
   */
  static async sendAuctionStartingNotification(data: {
    userEmail: string;
    userId?: string;
    userName: string;
    auctionTitle: string;
    startTime: string;
    location: string;
    auctionId: string;
  }): Promise<EmailNotificationResult> {
    return this.sendNotification('auction_starting', {
      to: data.userEmail,
      userId: data.userId,
      variables: {
        user_name: data.userName,
        auction_title: data.auctionTitle,
        start_time: data.startTime,
        location: data.location,
        auction_id: data.auctionId
      }
    });
  }

  /**
   * Send saved artwork confirmation (already working example)
   */
  static async sendSavedArtworkNotification(data: {
    userEmail: string;
    userId?: string;
    userName: string;
    artworkTitle: string;
    artistName: string;
    lotNumber: string;
    auctionTitle: string;
    startingBid: number;
    currentBid: number;
  }): Promise<EmailNotificationResult> {
    return this.sendNotification('artwork_saved', {
      to: data.userEmail,
      userId: data.userId,
      variables: {
        user_name: data.userName,
        artwork_title: data.artworkTitle,
        artist_name: data.artistName,
        lot_number: data.lotNumber,
        auction_title: data.auctionTitle,
        starting_bid: data.startingBid.toLocaleString(),
        current_bid: data.currentBid.toLocaleString()
      }
    });
  }

  /**
   * Send welcome email notification for new user registration
   */
  static async sendWelcomeNotification(data: {
    userEmail: string;
    userId?: string;
    userName: string;
    firstName?: string;
  }): Promise<EmailNotificationResult> {
    console.log('🚀 EmailService: Starting sendWelcomeNotification with data:', data);
    
    // Generate clean, modern welcome email template
    const welcomeTemplate = 
      '<!DOCTYPE html>' +
      '<html lang="en">' +
      '<head>' +
        '<meta charset="UTF-8">' +
        '<meta name="viewport" content="width=device-width, initial-scale=1.0">' +
        '<title>Welcome to Florida Highwaymen Auctions!</title>' +
      '</head>' +
      '<body style="margin: 0; padding: 0; background-color: #f9f9f9; font-family: -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, \'Helvetica Neue\', Arial, sans-serif;">' +
        '<div style="max-width: 600px; margin: 0 auto; background-color: #ffffff;">' +
          
          // Clean header
          '<div style="background-color: #ffffff; padding: 40px 40px 20px 40px; border-bottom: 1px solid #e5e5e5;">' +
            '<h1 style="margin: 0; color: #8B4513; font-size: 24px; font-weight: 300; letter-spacing: 2px; text-align: center;">FLORIDA HIGHWAYMEN AUCTIONS</h1>' +
            '<p style="margin: 8px 0 0 0; color: #666666; font-size: 14px; text-align: center; font-weight: 300;">Celebrating the Legacy of American Art</p>' +
          '</div>' +
          
          // Welcome header
          '<div style="background-color: #ffffff; padding: 30px 40px 10px 40px;">' +
            '<h2 style="margin: 0; color: #2d2d2d; font-size: 28px; font-weight: 400; text-align: center;">Welcome to Our Community!</h2>' +
            '<p style="margin: 15px 0 0 0; color: #8B4513; font-size: 16px; text-align: center; font-weight: 500;">Your journey into Florida Highwaymen art begins now</p>' +
          '</div>' +
          
          // Welcome badge
          '<div style="background-color: #ffffff; padding: 0 40px 20px 40px; text-align: center;">' +
            '<div style="display: inline-block; background-color: #8B4513; color: #ffffff; padding: 8px 20px; border-radius: 20px; font-size: 12px; font-weight: 600; letter-spacing: 1px;">NEW MEMBER</div>' +
          '</div>' +
          
          // Main content
          '<div style="padding: 20px 40px 40px 40px; background-color: #ffffff;">' +
            '<p style="margin: 0 0 30px 0; font-size: 16px; color: #2d2d2d; line-height: 1.6;">Dear {{user_name}},</p>' +
            '<p style="margin: 0 0 30px 0; font-size: 16px; color: #2d2d2d; line-height: 1.6;">Welcome to Florida Highwaymen Auctions! We\'re thrilled to have you join our community of art collectors and enthusiasts who share a passion for this unique American art movement.</p>' +
            
            // Florida Highwaymen info section
            '<div style="border: 1px solid #e5e5e5; border-radius: 8px; overflow: hidden; margin: 30px 0;">' +
              '<div style="background-color: #f8f8f8; padding: 15px 25px; border-bottom: 1px solid #e5e5e5;">' +
                '<h3 style="margin: 0; color: #8B4513; font-size: 18px; font-weight: 500;">About the Florida Highwaymen</h3>' +
              '</div>' +
              '<div style="padding: 25px;">' +
                '<p style="margin: 0 0 15px 0; color: #2d2d2d; font-size: 14px; line-height: 1.6;">The Florida Highwaymen were a group of 26 African American landscape artists who painted Florida landscapes and sold them from the trunks of their cars along the roadside in the 1950s and 1960s. Their vibrant, impressionistic works captured the beauty of Florida\'s wetlands, coastlines, and tropical landscapes.</p>' +
                '<p style="margin: 0; color: #2d2d2d; font-size: 14px; line-height: 1.6;">Today, these historically significant paintings are highly sought after by collectors worldwide, representing both artistic achievement and an important chapter in American art history.</p>' +
              '</div>' +
            '</div>' +
            
            // Getting started guide
            '<div style="background-color: #f8f8f8; border-left: 4px solid #8B4513; padding: 20px; margin: 30px 0;">' +
              '<h4 style="margin: 0 0 12px 0; color: #2d2d2d; font-size: 16px; font-weight: 500;">Getting Started</h4>' +
              '<ul style="margin: 0; padding-left: 18px; color: #666666; font-size: 14px; line-height: 1.6;">' +
                '<li style="margin-bottom: 8px;">Browse our current auctions and upcoming lots</li>' +
                '<li style="margin-bottom: 8px;">Add a payment method to start bidding on artwork</li>' +
                '<li style="margin-bottom: 8px;">Set up email notifications for lots you\'re interested in</li>' +
                '<li style="margin-bottom: 8px;">Join our live auction broadcasts for real-time bidding</li>' +
                '<li>Read our bidding guidelines and terms of service</li>' +
              '</ul>' +
            '</div>' +
            
            // Featured benefits
            '<div style="border: 1px solid #e5e5e5; border-radius: 8px; overflow: hidden; margin: 30px 0;">' +
              '<div style="background-color: #f8f8f8; padding: 15px 25px; border-bottom: 1px solid #e5e5e5;">' +
                '<h3 style="margin: 0; color: #8B4513; font-size: 18px; font-weight: 500;">Member Benefits</h3>' +
              '</div>' +
              '<div style="padding: 25px;">' +
                '<div style="margin-bottom: 15px;">' +
                  '<span style="display: inline-block; background-color: #8B4513; color: #ffffff; width: 20px; height: 20px; border-radius: 50%; text-align: center; line-height: 20px; font-size: 12px; font-weight: 600; margin-right: 10px;">✓</span>' +
                  '<span style="color: #2d2d2d; font-size: 14px; font-weight: 500;">Access to exclusive auctions and previews</span>' +
                '</div>' +
                '<div style="margin-bottom: 15px;">' +
                  '<span style="display: inline-block; background-color: #8B4513; color: #ffffff; width: 20px; height: 20px; border-radius: 50%; text-align: center; line-height: 20px; font-size: 12px; font-weight: 600; margin-right: 10px;">✓</span>' +
                  '<span style="color: #2d2d2d; font-size: 14px; font-weight: 500;">Expert authentication and condition reports</span>' +
                '</div>' +
                '<div style="margin-bottom: 15px;">' +
                  '<span style="display: inline-block; background-color: #8B4513; color: #ffffff; width: 20px; height: 20px; border-radius: 50%; text-align: center; line-height: 20px; font-size: 12px; font-weight: 600; margin-right: 10px;">✓</span>' +
                  '<span style="color: #2d2d2d; font-size: 14px; font-weight: 500;">Secure payment processing and worldwide shipping</span>' +
                '</div>' +
                '<div>' +
                  '<span style="display: inline-block; background-color: #8B4513; color: #ffffff; width: 20px; height: 20px; border-radius: 50%; text-align: center; line-height: 20px; font-size: 12px; font-weight: 600; margin-right: 10px;">✓</span>' +
                  '<span style="color: #2d2d2d; font-size: 14px; font-weight: 500;">Dedicated customer support for collectors</span>' +
                '</div>' +
              '</div>' +
            '</div>' +
            
            // Support message
            '<div style="background-color: #fff7ed; border: 1px solid #fed7aa; border-radius: 8px; padding: 20px; margin: 30px 0;">' +
              '<h4 style="margin: 0 0 12px 0; color: #9a3412; font-size: 16px; font-weight: 500;">Need Help Getting Started?</h4>' +
              '<p style="margin: 0; color: #9a3412; font-size: 14px; line-height: 1.6;">Our team is here to help! Whether you\'re a first-time collector or an experienced buyer, don\'t hesitate to reach out with any questions about our auctions, the bidding process, or Florida Highwaymen art in general.</p>' +
            '</div>' +
            
            // CTA Buttons
            '<div style="text-align: center; margin: 40px 0 30px 0;">' +
              '<a href="{{platform_url}}/auctions" style="display: inline-block; background-color: #8B4513; color: #ffffff; padding: 14px 32px; text-decoration: none; border-radius: 4px; font-size: 14px; font-weight: 500; letter-spacing: 1px; text-transform: uppercase; margin-right: 15px; margin-bottom: 10px;">Browse Current Auctions</a>' +
              '<a href="{{platform_url}}/dashboard" style="display: inline-block; background-color: #2d2d2d; color: #ffffff; padding: 14px 32px; text-decoration: none; border-radius: 4px; font-size: 14px; font-weight: 500; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 10px;">Complete Your Profile</a>' +
            '</div>' +
            
            '<p style="margin: 30px 0 0 0; font-size: 16px; color: #2d2d2d; line-height: 1.6; text-align: center;">Thank you for joining us in celebrating and preserving this remarkable art legacy!</p>' +
            '<p style="margin: 15px 0 0 0; font-size: 16px; color: #8B4513; line-height: 1.6; text-align: center; font-weight: 500;">— The Florida Highwaymen Auctions Team</p>' +
          '</div>' +
          
          // Footer
          '<div style="background-color: #f8f8f8; padding: 30px 40px; text-align: center; border-top: 1px solid #e5e5e5;">' +
            '<p style="margin: 0 0 8px 0; color: #2d2d2d; font-size: 14px; font-weight: 500;">Florida Highwaymen Auctions LLC</p>' +
            '<p style="margin: 0 0 15px 0; color: #666666; font-size: 12px;">8210 Griffin Rd, Davie, FL 33328</p>' +
            '<p style="margin: 0; color: #666666; font-size: 12px;">Questions? <a href="mailto:info@floridahighwaymenauction.com" style="color: #8B4513; text-decoration: none;">info@floridahighwaymenauction.com</a></p>' +
          '</div>' +
        '</div>' +
      '</body>' +
      '</html>';

    console.log('🔄 EmailService: Replacing template variables...');
    let htmlContent = welcomeTemplate
      .replace(/{{user_name}}/g, data.userName)
      .replace(/{{platform_url}}/g, this.PLATFORM_URL);
    
    console.log('✅ EmailService: Template variables replaced successfully');

    const subject = `Welcome to Florida Highwaymen Auctions, ${data.firstName || data.userName}!`;

    console.log('📧 EmailService: Calling sendCustomEmail with subject:', subject);
    return this.sendCustomEmail({
      to: data.userEmail,
      subject,
      htmlContent,
      emailType: 'welcome_new_user',
      userId: data.userId
    });
  }

  /**
   * Send custom email with template
   */
  static async sendCustomEmail(data: {
    to: string;
    subject: string;
    htmlContent: string;
    emailType: string;
    userId?: string;
  }): Promise<EmailNotificationResult> {
    console.log('🎯 EmailService: sendCustomEmail called with:', { to: data.to, subject: data.subject, emailType: data.emailType });
    
    try {
      // Email sending is now enabled - using Resend via Edge Function
      console.log('📧 EmailService: Sending email via Edge Function');
      console.log('📧 To:', data.to);
      console.log('📝 Subject:', data.subject);

      // Original Edge Function call (when BYPASS_MODE is false)
      const { data: result, error } = await supabase.functions.invoke('send-email-notification', {
        body: {
          to: data.to,
          subject: data.subject,
          html_content: data.htmlContent,
          email_type: data.emailType,
          user_id: data.userId
        }
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { 
        success: true, 
        messageId: result?.message_id 
      };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Send bulk notifications to multiple users
   */
  static async sendBulkNotifications(
    templateKey: string,
    recipients: Array<{
      email: string;
      userId?: string;
      variables: Record<string, any>;
    }>
  ): Promise<Array<{ email: string; result: EmailNotificationResult }>> {
    const results = await Promise.allSettled(
      recipients.map(async (recipient) => ({
        email: recipient.email,
        result: await this.sendNotification(templateKey, {
          to: recipient.email,
          userId: recipient.userId,
          variables: recipient.variables
        })
      }))
    );

    return results.map((result, index) => ({
      email: recipients[index].email,
      result: result.status === 'fulfilled' 
        ? result.value.result 
        : { success: false, error: 'Promise rejected' }
    }));
  }

  /**
   * Get user email preferences - ALWAYS ENABLED
   * All users receive all email types - no opt-out restrictions
   */
  static async getUserEmailPreferences(userId: string, emailType: string): Promise<boolean> {
    // 🚀 FORCE ALL EMAIL TYPES - Maximum engagement mode!
    console.log(`✅ Email preference check bypassed - ALL EMAILS ENABLED for user: ${userId}, type: ${emailType}`);
    return true; // Always send emails regardless of user preferences
  }

  /**
   * Check if user is within daily email limit - REMOVED ALL LIMITS
   * Now returns true always for unlimited emails
   */
  static async isWithinDailyLimit(userId: string): Promise<boolean> {
    // 🚀 UNLIMITED EMAILS MODE - No restrictions!
    console.log('✅ Email limit check bypassed - UNLIMITED mode enabled for user:', userId);
    return true; // Always allow emails - no limits!
  }

  /**
   * Send proxy bid setup confirmation email
   */
  static async sendProxyBidSetupConfirmation(data: {
    userEmail: string;
    userId?: string;
    userName: string;
    artworkTitle: string;
    artistName: string;
    lotNumber: string;
    maxBidAmount: number;
    auctionTitle: string;
    lotId: string;
    artworkImage?: string;
  }): Promise<EmailNotificationResult> {
    
    const proxyBidSetupTemplate = 
      '<!DOCTYPE html>' +
      '<html lang="en">' +
      '<head>' +
        '<meta charset="UTF-8">' +
        '<meta name="viewport" content="width=device-width, initial-scale=1.0">' +
        '<title>Proxy Bid Setup Confirmed</title>' +
      '</head>' +
      '<body style="margin: 0; padding: 0; font-family: \'Segoe UI\', Tahoma, Geneva, Verdana, sans-serif; background-color: #f5f5f5;">' +
        '<div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">' +
          
          // Header
          '<div style="background: linear-gradient(135deg, #8B4513 0%, #A0522D 100%); padding: 40px 40px 30px 40px; text-align: center;">' +
            '<h1 style="margin: 0; color: #ffffff; font-size: 28px; font-weight: 300; letter-spacing: 2px;">PROXY BID CONFIRMED</h1>' +
            '<p style="margin: 15px 0 0 0; color: #ffffff; font-size: 16px; opacity: 0.9;">Your automatic bidding is now active</p>' +
          '</div>' +
          
          // Main Content
          '<div style="padding: 40px;">' +
            '<p style="margin: 0 0 25px 0; color: #2d2d2d; font-size: 16px; line-height: 1.6;">Dear {{bidder_name}},</p>' +
            
            '<p style="margin: 0 0 30px 0; color: #2d2d2d; font-size: 16px; line-height: 1.8;">Your proxy bid has been successfully set up for the following lot. Our system will automatically bid on your behalf up to your maximum amount.</p>' +
            
            // Lot Details Card
            '<div style="background-color: #f8f8f8; border-radius: 8px; padding: 25px; margin: 30px 0; border-left: 5px solid #8B4513;">' +
              '<div style="display: flex; align-items: flex-start; gap: 20px;">' +
                '<img src="{{artwork_image}}" alt="{{lot_title}}" style="width: 100px; height: 100px; object-fit: cover; border-radius: 6px; border: 2px solid #e5e5e5;">' +
                '<div style="flex: 1;">' +
                  '<h3 style="margin: 0 0 10px 0; color: #2d2d2d; font-size: 18px; font-weight: 600;">{{lot_title}}</h3>' +
                  '<p style="margin: 0 0 15px 0; color: #8B4513; font-size: 14px; font-weight: 500;">by {{artist_name}}</p>' +
                  '<div style="display: flex; flex-direction: column; gap: 8px;">' +
                    '<div style="margin-bottom: 8px;">' +
                      '<span style="color: #666666; font-size: 14px; display: inline-block; width: 120px;">Lot Number:</span>' +
                      '<span style="color: #2d2d2d; font-size: 14px; font-weight: 500;">{{lot_number}}</span>' +
                    '</div>' +
                    '<div style="margin-bottom: 8px;">' +
                      '<span style="color: #666666; font-size: 14px; display: inline-block; width: 120px;">Your Max Bid:</span>' +
                      '<span style="color: #8B4513; font-size: 16px; font-weight: 600;">${{max_bid_amount}}</span>' +
                    '</div>' +
                    '<div>' +
                      '<span style="color: #666666; font-size: 14px; display: inline-block; width: 120px;">Auction:</span>' +
                      '<span style="color: #2d2d2d; font-size: 14px;">{{auction_title}}</span>' +
                    '</div>' +
                  '</div>' +
                '</div>' +
              '</div>' +
            '</div>' +
            
            // How It Works
            '<div style="background-color: #f0f8ff; border-left: 4px solid #4A90E2; padding: 20px; margin: 30px 0;">' +
              '<h4 style="margin: 0 0 12px 0; color: #2d2d2d; font-size: 16px; font-weight: 500;">How Proxy Bidding Works</h4>' +
              '<ul style="margin: 0; padding-left: 18px; color: #666666; font-size: 14px; line-height: 1.6;">' +
                '<li style="margin-bottom: 5px;">Our system will automatically bid for you when others bid</li>' +
                '<li style="margin-bottom: 5px;">We only bid the minimum increment needed to keep you in the lead</li>' +
                '<li style="margin-bottom: 5px;">Your maximum bid amount remains confidential</li>' +
                '<li>You\'ll be notified of all bidding activity via email</li>' +
              '</ul>' +
            '</div>' +
            
            // CTA Button
            '<div style="text-align: center; margin: 40px 0 30px 0;">' +
              '<a href="{{lot_url}}" style="display: inline-block; background-color: #8B4513; color: #ffffff; padding: 14px 32px; text-decoration: none; border-radius: 4px; font-size: 14px; font-weight: 500; letter-spacing: 1px; text-transform: uppercase;">Monitor Your Lot</a>' +
            '</div>' +
          '</div>' +
          
          // Footer
          '<div style="background-color: #f8f8f8; padding: 30px 40px; text-align: center; border-top: 1px solid #e5e5e5;">' +
            '<p style="margin: 0 0 8px 0; color: #2d2d2d; font-size: 14px; font-weight: 500;">Florida Highwaymen Auctions LLC</p>' +
            '<p style="margin: 0 0 15px 0; color: #666666; font-size: 12px;">8210 Griffin Rd, Davie, FL 33328</p>' +
            '<p style="margin: 0; color: #666666; font-size: 12px;">Questions? <a href="mailto:info@floridahighwaymenauction.com" style="color: #8B4513; text-decoration: none;">info@floridahighwaymenauction.com</a></p>' +
          '</div>' +
        '</div>' +
      '</body>' +
      '</html>';

    // Replace variables in template
    let htmlContent = proxyBidSetupTemplate
      .replace(/{{bidder_name}}/g, data.userName)
      .replace(/{{lot_title}}/g, data.artworkTitle)
      .replace(/{{artist_name}}/g, data.artistName)
      .replace(/{{lot_number}}/g, data.lotNumber)
      .replace(/{{max_bid_amount}}/g, data.maxBidAmount.toLocaleString())
      .replace(/{{auction_title}}/g, data.auctionTitle)
      .replace(/{{artwork_image}}/g, data.artworkImage || 'https://placehold.co/100x100/8B4513/ffffff/png?text=ARTWORK&font=roboto')
      .replace(/{{lot_url}}/g, `${this.PLATFORM_URL}/lots/${data.lotId}`);

    const subject = `🎯 Proxy Bid Activated - ${data.artworkTitle}`;

    return this.sendCustomEmail({
      to: data.userEmail,
      subject,
      htmlContent,
      emailType: 'proxy_bid_setup',
      userId: data.userId
    });
  }

  /**
   * Send proxy bid approval notification email
   */
  static async sendProxyBidApprovalNotification(data: {
    userEmail: string;
    userId?: string;
    userName: string;
    artworkTitle: string;
    artistName: string;
    lotNumber: string;
    maxBidAmount: number;
    auctionTitle: string;
    lotId: string;
    artworkImage?: string;
  }): Promise<EmailNotificationResult> {
    
    const proxyBidApprovalTemplate = 
      '<!DOCTYPE html>' +
      '<html lang="en">' +
      '<head>' +
        '<meta charset="UTF-8">' +
        '<meta name="viewport" content="width=device-width, initial-scale=1.0">' +
        '<title>Proxy Bid Approved</title>' +
      '</head>' +
      '<body style="margin: 0; padding: 0; font-family: \'Segoe UI\', Tahoma, Geneva, Verdana, sans-serif; background-color: #f5f5f5;">' +
        '<div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">' +
          
          // Header
          '<div style="background: linear-gradient(135deg, #22C55E 0%, #16A34A 100%); padding: 40px 40px 30px 40px; text-align: center;">' +
            '<h1 style="margin: 0; color: #ffffff; font-size: 28px; font-weight: 300; letter-spacing: 2px;">PROXY BID APPROVED</h1>' +
            '<p style="margin: 15px 0 0 0; color: #ffffff; font-size: 16px; opacity: 0.9;">Your automatic bidding is now LIVE</p>' +
          '</div>' +
          
          // Main Content
          '<div style="padding: 40px;">' +
            '<p style="margin: 0 0 25px 0; color: #2d2d2d; font-size: 16px; line-height: 1.6;">Dear {{bidder_name}},</p>' +
            
            '<p style="margin: 0 0 30px 0; color: #2d2d2d; font-size: 16px; line-height: 1.8;"><strong>Great news!</strong> Your proxy bid has been approved and is now actively monitoring the auction. Our system will begin bidding automatically on your behalf.</p>' +
            
            // Lot Details Card
            '<div style="background-color: #f0f8ff; border-radius: 8px; padding: 25px; margin: 30px 0; border-left: 5px solid #22C55E;">' +
              '<div style="display: flex; align-items: flex-start; gap: 20px;">' +
                '<img src="{{artwork_image}}" alt="{{lot_title}}" style="width: 100px; height: 100px; object-fit: cover; border-radius: 6px; border: 2px solid #e5e5e5;">' +
                '<div style="flex: 1;">' +
                  '<h3 style="margin: 0 0 10px 0; color: #2d2d2d; font-size: 18px; font-weight: 600;">{{lot_title}}</h3>' +
                  '<p style="margin: 0 0 15px 0; color: #8B4513; font-size: 14px; font-weight: 500;">by {{artist_name}}</p>' +
                  '<div style="display: flex; flex-direction: column; gap: 8px;">' +
                    '<div style="margin-bottom: 8px;">' +
                      '<span style="color: #666666; font-size: 14px; display: inline-block; width: 120px;">Lot Number:</span>' +
                      '<span style="color: #2d2d2d; font-size: 14px; font-weight: 500;">{{lot_number}}</span>' +
                    '</div>' +
                    '<div style="margin-bottom: 8px;">' +
                      '<span style="color: #666666; font-size: 14px; display: inline-block; width: 120px;">Your Max Bid:</span>' +
                      '<span style="color: #22C55E; font-size: 16px; font-weight: 600;">${{max_bid_amount}}</span>' +
                    '</div>' +
                    '<div>' +
                      '<span style="color: #666666; font-size: 14px; display: inline-block; width: 120px;">Status:</span>' +
                      '<span style="color: #22C55E; font-size: 14px; font-weight: 600;">ACTIVE & MONITORING</span>' +
                    '</div>' +
                  '</div>' +
                '</div>' +
              '</div>' +
            '</div>' +
            
            // What Happens Next
            '<div style="background-color: #f8f8f8; border-left: 4px solid #8B4513; padding: 20px; margin: 30px 0;">' +
              '<h4 style="margin: 0 0 12px 0; color: #2d2d2d; font-size: 16px; font-weight: 500;">What Happens Next</h4>' +
              '<ul style="margin: 0; padding-left: 18px; color: #666666; font-size: 14px; line-height: 1.6;">' +
                '<li style="margin-bottom: 5px;">Our system is now actively monitoring this lot</li>' +
                '<li style="margin-bottom: 5px;">We\'ll automatically place bids when others bid against you</li>' +
                '<li style="margin-bottom: 5px;">You\'ll receive immediate email alerts for each auto-bid</li>' +
                '<li>If you\'re outbid beyond your maximum, we\'ll notify you instantly</li>' +
              '</ul>' +
            '</div>' +
            
            // CTA Button
            '<div style="text-align: center; margin: 40px 0 30px 0;">' +
              '<a href="{{lot_url}}" style="display: inline-block; background-color: #22C55E; color: #ffffff; padding: 14px 32px; text-decoration: none; border-radius: 4px; font-size: 14px; font-weight: 500; letter-spacing: 1px; text-transform: uppercase;">Watch Live Bidding</a>' +
            '</div>' +
          '</div>' +
          
          // Footer
          '<div style="background-color: #f8f8f8; padding: 30px 40px; text-align: center; border-top: 1px solid #e5e5e5;">' +
            '<p style="margin: 0 0 8px 0; color: #2d2d2d; font-size: 14px; font-weight: 500;">Florida Highwaymen Auctions LLC</p>' +
            '<p style="margin: 0 0 15px 0; color: #666666; font-size: 12px;">8210 Griffin Rd, Davie, FL 33328</p>' +
            '<p style="margin: 0; color: #666666; font-size: 12px;">Questions? <a href="mailto:info@floridahighwaymenauction.com" style="color: #8B4513; text-decoration: none;">info@floridahighwaymenauction.com</a></p>' +
          '</div>' +
        '</div>' +
      '</body>' +
      '</html>';

    // Replace variables in template
    let htmlContent = proxyBidApprovalTemplate
      .replace(/{{bidder_name}}/g, data.userName)
      .replace(/{{lot_title}}/g, data.artworkTitle)
      .replace(/{{artist_name}}/g, data.artistName)
      .replace(/{{lot_number}}/g, data.lotNumber)
      .replace(/{{max_bid_amount}}/g, data.maxBidAmount.toLocaleString())
      .replace(/{{auction_title}}/g, data.auctionTitle)
      .replace(/{{artwork_image}}/g, data.artworkImage || 'https://placehold.co/100x100/8B4513/ffffff/png?text=ARTWORK&font=roboto')
      .replace(/{{lot_url}}/g, `${this.PLATFORM_URL}/lots/${data.lotId}`);

    const subject = `✅ Proxy Bid APPROVED & ACTIVE - ${data.artworkTitle}`;

    return this.sendCustomEmail({
      to: data.userEmail,
      subject,
      htmlContent,
      emailType: 'proxy_bid_approved',
      userId: data.userId
    });
  }

  /**
   * Send auto-bid execution notification email
   */
  static async sendAutoBidExecutionNotification(data: {
    userEmail: string;
    userId?: string;
    userName: string;
    artworkTitle: string;
    artistName: string;
    lotNumber: string;
    bidAmount: number;
    currentHighBid: number;
    remainingProxyAmount: number;
    auctionTitle: string;
    lotId: string;
    artworkImage?: string;
  }): Promise<EmailNotificationResult> {
    
    const autoBidExecutionTemplate = 
      '<!DOCTYPE html>' +
      '<html lang="en">' +
      '<head>' +
        '<meta charset="UTF-8">' +
        '<meta name="viewport" content="width=device-width, initial-scale=1.0">' +
        '<title>Auto-Bid Executed</title>' +
      '</head>' +
      '<body style="margin: 0; padding: 0; font-family: \'Segoe UI\', Tahoma, Geneva, Verdana, sans-serif; background-color: #f5f5f5;">' +
        '<div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">' +
          
          // Header
          '<div style="background: linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%); padding: 40px 40px 30px 40px; text-align: center;">' +
            '<h1 style="margin: 0; color: #ffffff; font-size: 28px; font-weight: 300; letter-spacing: 2px;">AUTO-BID EXECUTED</h1>' +
            '<p style="margin: 15px 0 0 0; color: #ffffff; font-size: 16px; opacity: 0.9;">We just bid ${bidAmount} on your behalf</p>' +
          '</div>' +
          
          // Main Content
          '<div style="padding: 40px;">' +
            '<p style="margin: 0 0 25px 0; color: #2d2d2d; font-size: 16px; line-height: 1.6;">Dear {{bidder_name}},</p>' +
            
            '<p style="margin: 0 0 30px 0; color: #2d2d2d; font-size: 16px; line-height: 1.8;">Your proxy bidding system just executed an automatic bid to keep you in the lead! Here are the details:</p>' +
            
            // Bid Details Card
            '<div style="background-color: #eff6ff; border-radius: 8px; padding: 25px; margin: 30px 0; border-left: 5px solid #3B82F6;">' +
              '<div style="display: flex; align-items: flex-start; gap: 20px;">' +
                '<img src="{{artwork_image}}" alt="{{lot_title}}" style="width: 100px; height: 100px; object-fit: cover; border-radius: 6px; border: 2px solid #e5e5e5;">' +
                '<div style="flex: 1;">' +
                  '<h3 style="margin: 0 0 10px 0; color: #2d2d2d; font-size: 18px; font-weight: 600;">{{lot_title}}</h3>' +
                  '<p style="margin: 0 0 15px 0; color: #8B4513; font-size: 14px; font-weight: 500;">by {{artist_name}}</p>' +
                  '<div style="display: flex; flex-direction: column; gap: 8px;">' +
                    '<div style="margin-bottom: 8px;">' +
                      '<span style="color: #666666; font-size: 14px; display: inline-block; width: 140px;">Auto-Bid Placed:</span>' +
                      '<span style="color: #3B82F6; font-size: 18px; font-weight: 700;">${{bid_amount}}</span>' +
                    '</div>' +
                    '<div style="margin-bottom: 8px;">' +
                      '<span style="color: #666666; font-size: 14px; display: inline-block; width: 140px;">Current High Bid:</span>' +
                      '<span style="color: #2d2d2d; font-size: 14px; font-weight: 500;">${{current_high_bid}}</span>' +
                    '</div>' +
                    '<div style="margin-bottom: 8px;">' +
                      '<span style="color: #666666; font-size: 14px; display: inline-block; width: 140px;">Remaining Budget:</span>' +
                      '<span style="color: #22C55E; font-size: 14px; font-weight: 600;">${{remaining_proxy_amount}}</span>' +
                    '</div>' +
                    '<div>' +
                      '<span style="color: #666666; font-size: 14px; display: inline-block; width: 140px;">Status:</span>' +
                      '<span style="color: #22C55E; font-size: 14px; font-weight: 600;">YOU\'RE THE HIGH BIDDER</span>' +
                    '</div>' +
                  '</div>' +
                '</div>' +
              '</div>' +
            '</div>' +
            
            // Status Update
            '<div style="background-color: #f0fdf4; border-left: 4px solid #22C55E; padding: 20px; margin: 30px 0;">' +
              '<h4 style="margin: 0 0 12px 0; color: #2d2d2d; font-size: 16px; font-weight: 500;">Good News!</h4>' +
              '<ul style="margin: 0; padding-left: 18px; color: #666666; font-size: 14px; line-height: 1.6;">' +
                '<li style="margin-bottom: 5px;">You are currently the highest bidder on this lot</li>' +
                '<li style="margin-bottom: 5px;">Your proxy bid is still active and monitoring</li>' +
                '<li style="margin-bottom: 5px;">We\'ll continue bidding automatically if others bid against you</li>' +
                '<li>You\'ll be notified if your remaining budget runs out</li>' +
              '</ul>' +
            '</div>' +
            
            // CTA Button
            '<div style="text-align: center; margin: 40px 0 30px 0;">' +
              '<a href="{{lot_url}}" style="display: inline-block; background-color: #3B82F6; color: #ffffff; padding: 14px 32px; text-decoration: none; border-radius: 4px; font-size: 14px; font-weight: 500; letter-spacing: 1px; text-transform: uppercase;">View Live Auction</a>' +
            '</div>' +
          '</div>' +
          
          // Footer
          '<div style="background-color: #f8f8f8; padding: 30px 40px; text-align: center; border-top: 1px solid #e5e5e5;">' +
            '<p style="margin: 0 0 8px 0; color: #2d2d2d; font-size: 14px; font-weight: 500;">Florida Highwaymen Auctions LLC</p>' +
            '<p style="margin: 0 0 15px 0; color: #666666; font-size: 12px;">8210 Griffin Rd, Davie, FL 33328</p>' +
            '<p style="margin: 0; color: #666666; font-size: 12px;">Questions? <a href="mailto:info@floridahighwaymenauction.com" style="color: #8B4513; text-decoration: none;">info@floridahighwaymenauction.com</a></p>' +
          '</div>' +
        '</div>' +
      '</body>' +
      '</html>';

    // Replace variables in template
    let htmlContent = autoBidExecutionTemplate
      .replace(/{{bidder_name}}/g, data.userName)
      .replace(/{{lot_title}}/g, data.artworkTitle)
      .replace(/{{artist_name}}/g, data.artistName)
      .replace(/{{lot_number}}/g, data.lotNumber)
      .replace(/{{bid_amount}}/g, data.bidAmount.toLocaleString())
      .replace(/{{current_high_bid}}/g, data.currentHighBid.toLocaleString())
      .replace(/{{remaining_proxy_amount}}/g, data.remainingProxyAmount.toLocaleString())
      .replace(/{{auction_title}}/g, data.auctionTitle)
      .replace(/{{artwork_image}}/g, data.artworkImage || 'https://placehold.co/100x100/8B4513/ffffff/png?text=ARTWORK&font=roboto')
      .replace(/{{lot_url}}/g, `${this.PLATFORM_URL}/lots/${data.lotId}`);

    const subject = `🤖 Auto-Bid Executed: $${data.bidAmount.toLocaleString()} - ${data.artworkTitle}`;

    return this.sendCustomEmail({
      to: data.userEmail,
      subject,
      htmlContent,
      emailType: 'auto_bid_executed',
      userId: data.userId
    });
  }
}

export default EmailNotificationService; 